import { useEffect, useState } from "react";
import FlexRow from "../../../components/base/FlexRow";
import Icon from "../../../components/base/Icon";
import { DrawerForm } from "../../../components/drawers/DrawerForm";
import ToggleSwitch from "../../../components/form/ToggleSwitch";
import { API_ROUTES } from "../../../core/Constants";
import { closeChatAccess, useHub, useScreen } from "../../../core/Global";
import ChatContactItem from "../models/ChatContactItem";
import Div from "../../../components/base/Div";
import Indicator from "../../../components/base/Indicator";
import { CompactUser } from "../../../core/CompactModels";
import Avatar from "../../../components/base/Avatar";
import { UserTypes } from "../../../core/Enums";
import { toastSuccess } from "../../../components/base/Toast";
import { UpdateChatAccessRequest } from "../models/UpdateChatAccessRequest ";

interface IProps {
    chat: ChatContactItem
}

export default function ChatUsersAccess(props: IProps) {

    const hub = useHub()
    const screen = useScreen()
    const [model, modelSet] = useState<UpdateChatAccessRequest>()
    const [loading, loadingSet] = useState<boolean>(true)

    const [users, usersSet] = useState<CompactUser[]>()

    useEffect(() => {
        if (props.chat.id) {
            hub.Get(`${API_ROUTES.Chat}item/${props.chat.id}`, res => {
                modelSet({
                    id: res.data.id,
                    accessAll: res.data.accessAll,
                    accessClaimUserIds: res.data.accessClaimUserIds,
                    accessReadUserIds: res.data.accessReadUserIds,
                    accessSendUserIds: res.data.accessSendUserIds
                })
            })
        }
    }, [props.chat])

    useEffect(() => {
        hub.Get(API_ROUTES.Chat + "usersForAccess", res => usersSet(res.data))
    }, [])

    useEffect(() => {
        if (model && users && loading) {
            loadingSet(false)
        }
    }, [model, users])


    return (
        <DrawerForm id="drawer-chat-users-access"
            title={`Chat Access`}
            onClose={closeChatAccess}
            buttonText="Update Chat Access"
            hideButtons={loading}
            onSubmit={(body, callback) => {

                hub.Post(API_ROUTES.Chat + "updateChatAccess", model, res => {
                    if (res.success) {
                        toastSuccess("Chat access settings updated successfully!")
                    }
                    callback!(res)
                })
            }}>
            <div>

                <Div className="my-20" visible={loading}>
                    <Indicator />
                </Div>

                <Div visible={!loading}>
                    <FlexRow className="mb-10">
                        <Icon icon="comment-lines" />
                        <span className="ms-2 fs-5">
                            {props.chat.title}
                        </span>
                    </FlexRow>

                    <div className="mb-10">
                        <ToggleSwitch id="toggle-chat-access-all"
                            label="Allow all users to access this chat!"
                            small raw checked={model?.accessAll}
                            onChange={e => {
                                modelSet(prev => ({ ...prev, accessAll: e }))
                            }} />
                    </div>

                    {
                        !model?.accessAll &&
                        <div className="scroll-y mb-10" style={{
                            height: (screen.height - 400)
                        }}>
                            <table className="table table-striped w-100">
                                <thead>
                                    <tr>
                                        <th>
                                            User
                                        </th>
                                        <th className="action-col w-125px">
                                            <a href="#" className="fw-bold"
                                                onClick={e => {
                                                    e.preventDefault();
                                                    modelSet(prev => ({ ...prev, accessReadUserIds: (prev?.accessReadUserIds?.length ?? 0) > 0 ? [] : users?.map(u => u.id!) }))
                                                }}>
                                                Read
                                            </a>
                                        </th>
                                        {
                                            // Nobody writes in a read-only chat, so there is
                                            // nothing to grant beyond reading it.
                                            !props.chat?.readOnly &&
                                            <>
                                                <th className="action-col w-125px">
                                                    <a href="#" className="fw-bold"
                                                        onClick={e => {
                                                            e.preventDefault();
                                                            modelSet(prev => ({ ...prev, accessSendUserIds: (prev?.accessSendUserIds?.length ?? 0) > 0 ? [] : users?.map(u => u.id!) }))
                                                        }}>
                                                        Send
                                                    </a>
                                                </th>
                                                <th className="action-col w-125px">
                                                    <a href="#" className="fw-bold"
                                                        onClick={e => {
                                                            e.preventDefault();
                                                            modelSet(prev => ({ ...prev, accessClaimUserIds: (prev?.accessClaimUserIds?.length ?? 0) > 0 ? [] : users?.map(u => u.id!) }))
                                                        }}>
                                                        Claim
                                                    </a>
                                                </th>
                                            </>
                                        }
                                    </tr>
                                </thead>

                                <tbody>
                                    {
                                        users?.map(user =>
                                            <tr key={`user-access-row-${user.id}`}>
                                                <td>
                                                    <FlexRow>
                                                        <Avatar avatarUrl={user.avatarUrl} fullName={user.fullName}
                                                            width={25} textSize={12} />
                                                        <div className="ms-5">
                                                            <div>
                                                                {user.fullName}
                                                            </div>
                                                            <div className="fs-8 text-muted">
                                                                {user.userType == UserTypes.Admin ? "Admin" : "Dispatcher"}
                                                            </div>
                                                        </div>
                                                    </FlexRow>
                                                </td>
                                                <td>
                                                    <ToggleSwitch key={`access-read-${user.id}`} id={`access-read-${user.id}`}
                                                        small raw checked={model?.accessReadUserIds?.includes(user.id!)}
                                                        onChange={e => {
                                                            if (e) modelSet(prev => ({
                                                                ...prev,
                                                                accessReadUserIds: prev?.accessReadUserIds?.includes(user.id!) ? prev.accessReadUserIds
                                                                    : [...prev?.accessReadUserIds ?? [], user.id!]
                                                            }))
                                                            else modelSet(prev => ({ ...prev, accessReadUserIds: prev?.accessReadUserIds?.filter(x => x !== user.id) }))
                                                        }} />
                                                </td>
                                                {
                                                    !props.chat?.readOnly &&
                                                    <>
                                                <td>
                                                    <ToggleSwitch key={`access-send-${user.id}`} id={`access-send-${user.id}`}
                                                        small raw checked={model?.accessSendUserIds?.includes(user.id!)}
                                                        onChange={e => {
                                                            if (e) {
                                                                modelSet(prev => ({
                                                                    ...prev,
                                                                    accessReadUserIds: prev?.accessReadUserIds?.includes(user.id!) ? prev.accessReadUserIds
                                                                        : [...prev?.accessReadUserIds ?? [], user.id!],
                                                                    accessSendUserIds: prev?.accessSendUserIds?.includes(user.id!) ? prev.accessSendUserIds
                                                                        : [...prev?.accessSendUserIds ?? [], user.id!]
                                                                }))
                                                            }
                                                            else modelSet(prev => ({ ...prev, accessSendUserIds: prev?.accessSendUserIds?.filter(x => x !== user.id) }))
                                                        }} />
                                                </td>

                                                <td>
                                                    <ToggleSwitch key={`access-claim-${user.id}`} id={`access-claim-${user.id}`}
                                                        small raw checked={model?.accessClaimUserIds?.includes(user.id!)}
                                                        onChange={e => {
                                                            if (e) {
                                                                modelSet(prev => ({
                                                                    ...prev,
                                                                    accessReadUserIds: prev?.accessReadUserIds?.includes(user.id!) ? prev.accessReadUserIds
                                                                        : [...prev?.accessReadUserIds ?? [], user.id!],
                                                                    accessClaimUserIds: prev?.accessClaimUserIds?.includes(user.id!) ? prev.accessClaimUserIds
                                                                        : [...prev?.accessClaimUserIds ?? [], user.id!]
                                                                }))
                                                            }
                                                            else modelSet(prev => ({ ...prev, accessClaimUserIds: prev?.accessClaimUserIds?.filter(x => x !== user.id) }))
                                                        }} />
                                                </td>
                                                    </>
                                                }
                                            </tr>
                                        )
                                    }

                                </tbody>
                            </table>
                        </div>
                    }





                </Div>



            </div>
        </DrawerForm>
    )
}